import { type BuildKindType } from '@common/types/common';
import { type RequirementsItemProps } from '@common/types/components';
import {
  type UserFactory,
  type UserFactoryNextToBuild,
} from '@common/types/factoryType';
import {
  type AvailableQuarry,
  type UserQuarry,
} from '@common/types/quarryType';
import { createSelector } from '@reduxjs/toolkit';

import {
  factoryTypesArray,
  type FactoryTypeText,
} from '@/common/types/factory';
import { plantTypesArray, type PlantTypeText } from '@/common/types/plant';
import {
  resourceTypesArray,
  type ResourceTypeText,
} from '@/common/types/resource';
import {
  type UserWorkshop,
  type UserWorkshopNextToBuild,
} from '@/common/types/workshopType';

import { type RootState } from '../store';

import {
  selectFactoryNextToBuild,
  selectFactoryNextToBuildRequirements,
} from './factoryReducer';
import {
  selectQuarryBuildRequirementsByResourceId,
  selectQuarryNextToBuild,
} from './quarrySelectors';
import { selectWorkshopNextToBuild } from './workshopSelectors';

const selectQuarriesList = (state: RootState): UserQuarry[] | undefined =>
  state.quarry.quarries?.userQuarries;

const selectPlantList = (state: RootState): UserFactory[] | null =>
  state.factory.list;
const selectWorkShopList = (state: RootState): UserWorkshop[] | null =>
  state.workshop.list;

const buildKindSelector = (_: RootState, kind: BuildKindType): BuildKindType =>
  kind;
const plantSelector = (
  _: RootState,
  plant: ResourceTypeText | PlantTypeText | FactoryTypeText,
): ResourceTypeText | PlantTypeText | FactoryTypeText => plant;

/* eslint max-params: ["error", 4] */
const selectNextToBuildByBuidlKind = createSelector(
  [
    selectQuarryNextToBuild,
    selectFactoryNextToBuild,
    selectWorkshopNextToBuild,
    plantSelector,
  ],
  (
    quarry: AvailableQuarry | undefined,
    factory: UserFactoryNextToBuild | null,
    workshop: UserWorkshopNextToBuild | null,
    plant: ResourceTypeText | PlantTypeText | FactoryTypeText,
  ):
    | AvailableQuarry
    | UserFactoryNextToBuild
    | UserWorkshopNextToBuild
    | undefined => {
    if (resourceTypesArray.includes(plant as ResourceTypeText)) {
      return quarry;
    }

    if (plantTypesArray.includes(plant as PlantTypeText)) {
      return factory || undefined;
    }

    if (factoryTypesArray.includes(plant as FactoryTypeText)) {
      return workshop || undefined;
    }
  },
);

const selectNextToBuildRequirementsByBuilKind = createSelector(
  [
    selectQuarryBuildRequirementsByResourceId,
    selectFactoryNextToBuildRequirements,
    buildKindSelector,
  ],
  (
    quarry: RequirementsItemProps[] | undefined,
    factory: RequirementsItemProps[] | undefined,
    kind: BuildKindType,
  ): RequirementsItemProps[] | undefined => {
    if (kind === 'quarry') {
      return quarry;
    }

    if (kind === 'factory') {
      return factory || undefined;
    }
  },
);

const selectCombinedResources = createSelector(
  selectQuarriesList,
  selectPlantList,
  selectWorkShopList,
  (userQuarries = [], userPlants = [], userWorkShops = []) => [
    ...(userQuarries || []),
    ...(userPlants || []),
    ...(userWorkShops || []),
  ],
);

const selectQuarryByResourceNameOrId = createSelector(
  selectCombinedResources,
  (
    _: RootState,
    resourceKey:
      | ResourceTypeText
      | PlantTypeText
      | FactoryTypeText
      | string
      | undefined,
  ): ResourceTypeText | PlantTypeText | FactoryTypeText | string | undefined =>
    resourceKey,
  (combinedResources, resourceKey) =>
    resourceKey && combinedResources
      ? combinedResources.find(
          ({ name, id }) => name === resourceKey || id === resourceKey,
        )
      : undefined,
);

export {
  selectNextToBuildByBuidlKind,
  selectNextToBuildRequirementsByBuilKind,
  selectQuarryByResourceNameOrId,
};
